/**
 * @file: PaneHeader.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Header bar for an embedded desktop inbox pane. On mobile the stack
 * navigator draws the header; on desktop the panes have no stack, so
 * each pane renders this instead. Clean white band matching the inbox
 * flow palette (#fff bg, #111 title, hairline bottom).
 *
 * Title is left-aligned with an optional small subtitle beneath it
 * (used for the chat "typing…" line). A `right` slot hosts pane
 * actions (e.g. the ⋯ group-info toggle); `onClose` renders a trailing
 * close button (used by the group-info column + overlays).
 */

import { Typography } from '@euler/app/components/typography';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChevronLeft, X } from 'lucide-react-native';
import { FC, ReactNode, memo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

const kHitSlop = { top: 8, right: 8, bottom: 8, left: 8 };

export const PaneHeader: FC<{
  title: string;
  subtitle?: string;
  right?: ReactNode;
  /** Small element rendered immediately before the title (e.g. an online dot). */
  titleAccessory?: ReactNode;
  /** Small label rendered INLINE after the title (e.g. 在线 / typing) — sits on
   * the name's line, so it stays aligned with the name (vs. `subtitle`, which
   * goes on a second line indented under the title). */
  titleSuffix?: ReactNode;
  /** Leading back button (e.g. an in-pane sub-page); rendered before title. */
  onBack?: () => void;
  onClose?: () => void;
  align?: 'left' | 'center';
}> = memo(
  ({
    title,
    subtitle,
    right,
    titleAccessory,
    titleSuffix,
    onBack,
    onClose,
    align = 'left',
  }) => {
    const { rpx } = useResponsiveContext();
    const centered = align === 'center';

    const titleBlock = (
      <>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {titleAccessory ? (
            <View style={{ marginRight: rpx(5) }}>{titleAccessory}</View>
          ) : null}
          <Typography.Text
            numberOfLines={1}
            size={rpx(16)}
            color="#111"
            style={{ fontFamily: FontFamily.NotoSans.Bold, flexShrink: 1 }}
          >
            {title}
          </Typography.Text>
          {titleSuffix ? (
            <View style={{ marginLeft: rpx(6) }}>{titleSuffix}</View>
          ) : null}
        </View>
        {subtitle ? (
          <Typography.Text
            numberOfLines={1}
            size={rpx(11)}
            color="rgba(17, 17, 17, 0.45)"
            style={{ marginTop: rpx(2), fontFamily: FontFamily.NotoSans.Light }}
          >
            {subtitle}
          </Typography.Text>
        ) : null}
      </>
    );

    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          height: rpx(56),
          paddingHorizontal: rpx(16),
          backgroundColor: '#fff',
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: '#ececec',
        }}
      >
        {onBack ? (
          <TouchableOpacity
            onPress={onBack}
            hitSlop={kHitSlop}
            style={{ marginRight: rpx(8), padding: rpx(4) }}
          >
            <ChevronLeft size={rpx(22)} color="#111" />
          </TouchableOpacity>
        ) : null}
        {/* Centered title is absolutely positioned across the FULL width so the
          asymmetric left (back) / right (actions + close) clusters don't pull
          it off-center; the row below just lays out the buttons. */}
        {centered ? (
          <View style={{ flex: 1 }} />
        ) : (
          <View
            style={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'flex-start',
            }}
          >
            {titleBlock}
          </View>
        )}
        {right ? <View style={{ marginLeft: rpx(8) }}>{right}</View> : null}
        {onClose ? (
          <TouchableOpacity
            onPress={onClose}
            hitSlop={kHitSlop}
            style={{ marginLeft: rpx(8), padding: rpx(4) }}
          >
            <X size={rpx(20)} color="#6b7280" />
          </TouchableOpacity>
        ) : null}
        {centered ? (
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              alignItems: 'center',
              justifyContent: 'center',
              paddingHorizontal: rpx(96),
            }}
          >
            {titleBlock}
          </View>
        ) : null}
      </View>
    );
  },
);
